import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Clock, LogOut, RefreshCw, Trash2, X } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";

const STATUS_META = {
  pending: { label: "Na čekanju", color: "text-[#D4AF37] border-[#D4AF37]/50" },
  confirmed: { label: "Potvrđeno", color: "text-emerald-300 border-emerald-400/50" },
  cancelled: { label: "Otkazano", color: "text-red-300 border-red-400/50" },
};

const FILTERS = [
  { value: "all", label: "Sve" },
  { value: "pending", label: "Na čekanju" },
  { value: "confirmed", label: "Potvrđene" },
  { value: "cancelled", label: "Otkazane" },
];

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/reservations");
      setItems(data);
    } catch (e) {
      toast.error("Greška", { description: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/reservations/${id}`, { status });
      setItems((prev) => prev.map((r) => (r.id === id ? data : r)));
      toast.success(`Rezervacija označena: ${STATUS_META[status].label}`);
    } catch (e) {
      toast.error("Greška", { description: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Sigurno obrisati rezervaciju?")) return;
    try {
      await api.delete(`/admin/reservations/${id}`);
      setItems((prev) => prev.filter((r) => r.id !== id));
      toast.success("Rezervacija obrisana");
    } catch (e) {
      toast.error("Greška", { description: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const filtered = items.filter((r) => (filter === "all" ? true : r.status === filter));

  const counts = items.reduce((acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; }, {});

  return (
    <div className="min-h-screen bg-emerald-deep text-[#F5EFE3]" data-testid="admin-dashboard">
      <header className="border-b border-gold/15 bg-emerald-darker/80 backdrop-blur sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between gap-6">
          <Logo size="sm" />
          <div className="flex items-center gap-4 md:gap-6">
            <button onClick={load} data-testid="refresh-btn" className="font-sans tracking-wide-2 text-[0.65rem] uppercase text-[#B0C4B1] hover:text-gold inline-flex items-center gap-2 transition">
              <RefreshCw className="w-3.5 h-3.5" /> Osvježi
            </button>
            <span className="hidden md:block font-sans text-xs text-[#B0C4B1]">{user?.email}</span>
            <button onClick={handleLogout} data-testid="logout-btn" className="font-sans tracking-wide-2 text-[0.65rem] uppercase text-gold hover:text-[#E6C98A] inline-flex items-center gap-2 transition">
              <LogOut className="w-3.5 h-3.5" /> Odjava
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16">
        <span className="font-sans tracking-luxury text-[0.65rem] uppercase text-gold/80">— Admin · Rezervacije</span>
        <h1 className="mt-4 font-serif font-light text-4xl md:text-5xl text-[#F5EFE3]" data-testid="dashboard-title">
          Pregled <em className="text-gold not-italic">termina</em>
        </h1>

        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { key: "all", label: "Ukupno", value: items.length, hue: "text-gold" },
            { key: "pending", label: "Na čekanju", value: counts.pending || 0, hue: "text-[#D4AF37]" },
            { key: "confirmed", label: "Potvrđeno", value: counts.confirmed || 0, hue: "text-emerald-300" },
            { key: "cancelled", label: "Otkazano", value: counts.cancelled || 0, hue: "text-red-300" },
          ].map((s) => (
            <div key={s.key} className="border border-gold/15 bg-emerald-darker/50 p-6" data-testid={`stat-${s.key}`}>
              <p className="font-sans tracking-wide-2 text-[0.65rem] uppercase text-[#B0C4B1]">{s.label}</p>
              <p className={`mt-3 font-serif text-4xl ${s.hue}`}>{s.value}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              data-testid={`filter-${f.value}`}
              className={`px-5 py-2 font-sans tracking-wide-2 text-[0.65rem] uppercase border transition ${
                filter === f.value
                  ? "border-gold bg-gold text-emerald-darker"
                  : "border-gold/30 text-[#F5EFE3]/80 hover:border-gold hover:text-gold"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="mt-8 border border-gold/15">
          {loading ? (
            <div className="p-16 text-center font-sans tracking-luxury text-xs text-gold/70">UČITAVANJE…</div>
          ) : filtered.length === 0 ? (
            <div className="p-16 text-center font-serif text-xl text-[#B0C4B1]">Nema rezervacija u ovoj kategoriji.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-emerald-darker/60 border-b border-gold/15">
                  <tr className="text-left font-sans tracking-wide-2 text-[0.6rem] uppercase text-gold/80">
                    <th className="px-5 py-4">Klijent</th>
                    <th className="px-5 py-4 hidden md:table-cell">Kontakt</th>
                    <th className="px-5 py-4">Usluga</th>
                    <th className="px-5 py-4">Termin</th>
                    <th className="px-5 py-4">Status</th>
                    <th className="px-5 py-4 text-right">Akcije</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr key={r.id} data-testid={`row-${r.id}`} className="border-b border-gold/10 hover:bg-emerald-darker/40 transition">
                      <td className="px-5 py-5">
                        <p className="font-serif text-lg text-[#F5EFE3]">{r.name}</p>
                        {r.note && <p className="font-sans text-xs text-[#B0C4B1]/70 mt-1 line-clamp-1 max-w-xs">{r.note}</p>}
                      </td>
                      <td className="px-5 py-5 hidden md:table-cell">
                        <p className="font-sans text-sm text-[#F5EFE3]">{r.phone}</p>
                        <p className="font-sans text-xs text-[#B0C4B1]">{r.email}</p>
                      </td>
                      <td className="px-5 py-5 font-sans text-sm text-[#F5EFE3]">{r.service_name}</td>
                      <td className="px-5 py-5">
                        <p className="font-serif text-base text-gold">{r.date}</p>
                        <p className="font-sans text-xs text-[#B0C4B1]">{r.time}</p>
                      </td>
                      <td className="px-5 py-5">
                        <span className={`inline-block px-3 py-1 font-sans tracking-wide-2 text-[0.6rem] uppercase border ${STATUS_META[r.status]?.color}`}>
                          {STATUS_META[r.status]?.label || r.status}
                        </span>
                      </td>
                      <td className="px-5 py-5">
                        <div className="flex items-center justify-end gap-2">
                          {r.status !== "confirmed" && (
                            <button title="Potvrdi" onClick={() => updateStatus(r.id, "confirmed")} data-testid={`confirm-${r.id}`} className="p-2 border border-emerald-400/30 text-emerald-300 hover:bg-emerald-400/10 transition">
                              <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                          )}
                          {r.status !== "pending" && (
                            <button title="Vrati na čekanje" onClick={() => updateStatus(r.id, "pending")} data-testid={`pending-${r.id}`} className="p-2 border border-gold/40 text-gold hover:bg-gold/10 transition">
                              <Clock className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                          )}
                          {r.status !== "cancelled" && (
                            <button title="Otkaži" onClick={() => updateStatus(r.id, "cancelled")} data-testid={`cancel-${r.id}`} className="p-2 border border-red-400/30 text-red-300 hover:bg-red-400/10 transition">
                              <X className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                          )}
                          <button title="Obriši" onClick={() => remove(r.id)} data-testid={`delete-${r.id}`} className="p-2 border border-red-400/30 text-red-300 hover:bg-red-400/20 transition">
                            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
